import { useState, useMemo } from "react";
import { Box, Typography, IconButton, Paper, Grid, Button, Fade, Tooltip } from "@mui/material";
import { ChevronLeft, ChevronRight, EditCalendar } from "@mui/icons-material";
import { colors } from "../../ui/colors";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const QUARTERS = ["Q1 (Jan-Mar)", "Q2 (Apr-Jun)", "Q3 (Jul-Sep)", "Q4 (Oct-Dec)"];

const startOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const periodLabelFor = (mode: string, isRange: boolean, start: Date) => {
  if (isRange) return "ALL TASKS IN RANGE";

  if (mode === "day") {
    return startOfDay(start).getTime() === startOfDay(new Date()).getTime() ? "ALL TASKS TODAY" : "ALL TASKS THIS DAY";
  }

  if (mode === "week") return "ALL TASKS THIS WEEK";
  if (mode === "month") return "ALL TASKS THIS MONTH";
  if (mode === "quarter") return "ALL TASKS THIS QUARTER";
  if (mode === "year") return "ALL TASKS THIS YEAR";
  return "ALL TASKS";
};

interface CalendarTimeSheetProps {
  onFilter: (range: { start: string | null; end: string | null }) => void;
  onPeriodChange?: (label: string) => void;
  width?: number | string;
  height?: number | string;
}

export default function CalendarTimeSheet({ onFilter, onPeriodChange, width = 300, height = "fit-content" }: CalendarTimeSheetProps) {
  const [viewDate, setViewDate] = useState(new Date());
  const [range, setRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
  const [mainMode, setMainMode] = useState("day");
  const [isRangePicker, setIsRangePicker] = useState(false);
  const [showCard, setShowCard] = useState(false);

  const disableColor = "#9c9999";
  const activeTextColor = "#374151";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const modeLabels: Record<string, string> = {
    day: "Day",
    week: "Week",
    month: "Month",
    quarter: "Quarter",
    year: "Year",
  };

  const isFuture = (date: Date) => date > today;

  const weeksInMonth = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const weeks = [];
    let current = new Date(year, month, 1);
    current.setDate(current.getDate() - current.getDay());
    while (current.getMonth() <= month || (current.getMonth() === 11 && month === 11) || weeks.length < 4) {
      const start = new Date(current);
      const end = new Date(current);
      end.setDate(end.getDate() + 6);
      weeks.push({ start, end });
      current.setDate(current.getDate() + 7);
      if (current.getMonth() > month && current.getFullYear() >= year && weeks.length >= 4) break;
    }
    return weeks;
  }, [viewDate]);

  const daysInMonth = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    const lastDay = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= lastDay; i++) days.push(new Date(year, month, i));
    return days;
  }, [viewDate]);

  const applyRange = (start: Date, end: Date, label: string) => {
    onFilter({
      start: startOfDay(start).toISOString(),
      end: endOfDay(end).toISOString(),
    });
    onPeriodChange?.(label);
  };

  const applyToday = () => {
    const todayStart = startOfDay(new Date());
    setMainMode("day");
    setViewDate(new Date());
    setIsRangePicker(false);
    setRange({ start: todayStart, end: todayStart });
    setShowCard(false);
    applyRange(todayStart, todayStart, "ALL TASKS TODAY");
  };

  const applyReset = () => {
    setRange({ start: null, end: null });
    setShowCard(false);
    onFilter({ start: null, end: null });
    onPeriodChange?.("ALL TASKS");
  };

  const handleSelection = (start: Date, end: Date) => {
    if (isFuture(start)) return;
    if (isRangePicker) {
      if (!range.start || (range.start && range.end)) {
        setRange({ start, end: null });
      } else {
        const finalRange = start < range.start ? { start, end: range.start } : { start: range.start, end };
        setRange(finalRange);
        setTimeout(() => setShowCard(true), 300);
      }
    } else {
      setRange({ start, end });
      setShowCard(true);
    }
  };

  const getStatus = (blockStart: Date, blockEnd?: Date) => {
    if (!range.start) return "normal";
    const sTime = blockStart.getTime();
    const eTime = (blockEnd || blockStart).getTime();
    const rStart = range.start.getTime();
    const rEnd = range.end?.getTime();
    if (sTime === rStart) return "start";
    if (rEnd && eTime === rEnd) return "end";
    if (rEnd && sTime > rStart && eTime < rEnd) return "in-range";
    return "normal";
  };

  const SelectionSummary = () => {
    if (!range.start) return null;
    const start = range.start;
    const end = range.end || range.start;
    const isSingleDay = start.getTime() === end.getTime();

    return (
      <Box sx={{ width: "100%", py: 1 }}>
        <Typography
          align="center"
          sx={{
            color: colors.orange.base,
            fontWeight: 900,
            fontSize: 10,
            mb: 3,
            textTransform: "uppercase",
            letterSpacing: 1,
          }}
        >
          {modeLabels[mainMode]} Selection
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: isSingleDay ? 0 : 5,
          }}
        >
          <Box textAlign="center">
            {!isSingleDay && <Typography sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", fontWeight: 900, fontSize: 10, mb: 0.5 }}>FROM</Typography>}
            <Typography
              sx={{
                fontSize: isSingleDay ? 56 : 38,
                fontWeight: 900,
                color: "var(--fx-surface-text, #0F172A)",
                letterSpacing: -1,
                lineHeight: 1.1,
              }}
            >
              {start.getDate()}
            </Typography>
            <Typography
              sx={{
                fontSize: isSingleDay ? 14 : 11,
                fontWeight: 800,
                color: "var(--fx-surface-textsecondary, #64748B)",
                mt: 0.5,
              }}
            >
              {MONTHS[start.getMonth()].slice(0, 3)} {start.getFullYear()}
            </Typography>
          </Box>

          {!isSingleDay && (
            <>
              <Box sx={{ height: 45, width: "2px", bgcolor: "var(--fx-surface-muted, #F1F5F9)", mx: 1 }} />

              <Box textAlign="center">
                <Typography sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", fontWeight: 900, fontSize: 10, mb: 0.5 }}>TO</Typography>
                <Typography
                  sx={{
                    fontSize: 38,
                    fontWeight: 900,
                    color: "var(--fx-surface-text, #0F172A)",
                    letterSpacing: -1,
                    lineHeight: 1.1,
                  }}
                >
                  {end.getDate()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "var(--fx-surface-textsecondary, #64748B)",
                    mt: 0.5,
                  }}
                >
                  {MONTHS[end.getMonth()].slice(0, 3)} {end.getFullYear()}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box display={"flex"} flexDirection={"column"} gap={1} justifyContent={"center"} alignItems={"center"}>
      <Box sx={{ bgcolor: "var(--fx-surface-base, #FFFFFF)", borderRadius: "12px", p: 0.5, width: "300px" }}>
        <Grid container spacing={0.5} wrap="nowrap">
          {Object.keys(modeLabels).map((m) => {
            const isSelected = mainMode === m;
            return (
              <Grid item xs key={m} sx={{ minWidth: 0 }}>
                <Button
                  fullWidth
                  size="small"
                  onClick={() => {
                    setMainMode(m);
                    setRange({ start: null, end: null });
                    setShowCard(false);
                  }}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: 13,
                    my: 0.5,
                    gap: 1,
                    px: 1,
                    py: 0.7,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    bgcolor: isSelected ? colors.orange.base : "transparent",
                    color: isSelected ? "#ffff" : "var(--fx-surface-textsecondary, #64748B)",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: isSelected ? colors.orange.base : "transparent", // Quita el color de fondo
                    },
                  }}
                >
                  {modeLabels[m]}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <Paper sx={{ p: 2, width, height, minHeight: 300, borderRadius: 5, bgcolor: "var(--fx-surface-base, #FFFFFF)", display: "flex", flexDirection: "column" }} elevation={0}>
        {!showCard ? (
          <Fade in={!showCard}>
            <Box>
              <Box sx={{ bgcolor: "var(--fx-surface-muted, #F1F5F9)", borderRadius: 3, p: 0.5, mb: 2 }}>
                <Grid container spacing={0.5}>
                  {[
                    { label: modeLabels[mainMode], val: false },
                    { label: "Range", val: true },
                  ].map((opt) => {
                    const isSelected = isRangePicker === opt.val;
                    return (
                      <Grid item xs={6} key={opt.label}>
                        <Button
                          fullWidth
                          size="small"
                          onClick={() => {
                            setIsRangePicker(opt.val);
                            setRange({ start: null, end: null });
                          }}
                          sx={{
                            borderRadius: 2.5,
                            textTransform: "none",
                            fontWeight: 800,
                            fontSize: 12,
                            py: 0.4,
                            bgcolor: isSelected ? colors.orange.base : "transparent",
                            color: isSelected ? "white" : "#777877",
                            boxShadow: "none",
                            "&:hover": {
                              bgcolor: isSelected ? colors.orange.base : "rgba(255, 107, 0, 0.08)", // Un toque de naranja muy sutil al pasar el mouse
                            },

                            "&.MuiButton-root": {
                              transition: "all 0.2s ease-in-out",
                            },
                          }}
                        >
                          {opt.label}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>

              {mainMode !== "year" && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <IconButton size="small" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}>
                    <ChevronLeft fontSize="small" />
                  </IconButton>
                  <Typography sx={{ fontWeight: 800, fontSize: 12 }}>
                    {mainMode === "quarter" ? viewDate.getFullYear() : `${MONTHS[viewDate.getMonth()]} ${viewDate.getFullYear()}`}
                  </Typography>
                  <IconButton size="small" onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}>
                    <ChevronRight fontSize="small" />
                  </IconButton>
                </Box>
              )}

              <Grid container spacing={1}>
                {mainMode === "week" &&
                  weeksInMonth.map((w, i) => {
                    const status = getStatus(w.start, w.end);
                    const active = status !== "normal";
                    const disabled = isFuture(w.start);
                    return (
                      <Grid item xs={12} key={i}>
                        <Tooltip title={disabled ? "Data not available" : ""} arrow>
                          <span style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%", display: "block" }}>
                            <Button
                              fullWidth
                              disabled={disabled}
                              onClick={() => handleSelection(w.start, w.end)}
                              sx={{
                                py: 0.8,
                                borderRadius: 2,
                                px: 2,
                                textTransform: "none",
                                display: "flex",
                                justifyContent: "space-between",
                                bgcolor: active ? colors.orange[100] : "var(--fx-surface-sunken, #F8FAFC)",
                                color: active ? colors.orange.base : activeTextColor,
                                "&:hover": { bgcolor: active ? colors.orange[100] : "rgba(255, 107, 0, 0.08)" },
                                "&:disabled": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)", color: disableColor },
                              }}
                            >
                              <Typography sx={{ fontSize: 11, fontWeight: 800 }}>Week {i + 1}</Typography>
                              <Typography sx={{ fontSize: 10, fontWeight: 700 }}>
                                {w.start.getDate()} - {w.end.getDate()} {MONTHS[w.end.getMonth()].slice(0, 3)}
                              </Typography>
                            </Button>
                          </span>
                        </Tooltip>
                      </Grid>
                    );
                  })}

                {mainMode === "month" &&
                  MONTHS.map((m, i) => {
                    const start = new Date(viewDate.getFullYear(), i, 1);
                    const status = getStatus(start, new Date(viewDate.getFullYear(), i + 1, 0));
                    const active = status !== "normal";
                    const disabled = isFuture(start);
                    return (
                      <Grid item xs={4} key={m}>
                        <Tooltip title={disabled ? "Future month" : ""} arrow>
                          <span style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%", display: "block" }}>
                            <Button
                              fullWidth
                              disabled={disabled}
                              onClick={() => handleSelection(start, new Date(viewDate.getFullYear(), i + 1, 0))}
                              sx={{
                                fontSize: 10,
                                fontWeight: 800,
                                bgcolor: active ? colors.orange[100] : "var(--fx-surface-sunken, #F8FAFC)",
                                color: active ? colors.orange.base : activeTextColor,
                                "&:hover": { bgcolor: active ? colors.orange[100] : "rgba(255, 107, 0, 0.08)" },
                                "&:disabled": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)", color: disableColor },
                              }}
                            >
                              {m.slice(0, 3)}
                            </Button>
                          </span>
                        </Tooltip>
                      </Grid>
                    );
                  })}

                {mainMode === "day" && (
                  <Grid container columns={7} sx={{ mt: 1, px: 1 }}>
                    {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                      <Grid item xs={1} key={d} textAlign="center">
                        <Typography sx={{ fontWeight: 800, color: "#c7cbd0", fontSize: 11 }}>{d}</Typography>
                      </Grid>
                    ))}
                    {daysInMonth.map((date, i) => {
                      if (!date) return <Grid item xs={1} key={i} />;
                      const disabled = isFuture(date);
                      const status = getStatus(date);
                      const isEndPoints = status === "start" || status === "end";
                      return (
                        <Grid item xs={1} key={i}>
                          <Tooltip title={disabled ? "No data" : ""} arrow>
                            <Box
                              onClick={() => !disabled && handleSelection(date, date)}
                              sx={{
                                height: 30,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: disabled ? "not-allowed" : "pointer",
                                fontSize: 11,
                                fontWeight: 800,
                                position: "relative",
                                backgroundColor: status === "in-range" || isEndPoints ? colors.orange[100] : disabled ? "var(--fx-surface-sunken, #F8FAFC)" : "transparent",
                                color: isEndPoints
                                  ? colors.orange.base
                                  : status === "in-range"
                                    ? colors.orange.base
                                    : disabled
                                      ? disableColor
                                      : activeTextColor,
                                border: disabled ? "1px solid var(--fx-surface-border, #E2E8F0)" : "none",
                                ...(status === "start" && { borderTopLeftRadius: "8px", borderBottomLeftRadius: "8px" }),
                                ...(status === "end" && { borderTopRightRadius: "8px", borderBottomRightRadius: "8px" }),
                              }}
                            >
                              {isEndPoints && (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    width: 22,
                                    height: 22,
                                    bgcolor: colors.orange.base,
                                    borderRadius: "8px",
                                    zIndex: -1,
                                  }}
                                />
                              )}
                              {date.getDate()}
                            </Box>
                          </Tooltip>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}

                {mainMode === "quarter" &&
                  QUARTERS.map((q, i) => {
                    const start = new Date(viewDate.getFullYear(), i * 3, 1);
                    const active = getStatus(start, new Date(viewDate.getFullYear(), (i + 1) * 3, 0)) !== "normal";
                    const disabled = isFuture(start);
                    return (
                      <Grid item xs={6} key={q}>
                        <Tooltip title={disabled ? "Future quarter" : ""} arrow>
                          <span style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%", display: "block" }}>
                            <Button
                              fullWidth
                              disabled={disabled}
                              onClick={() => handleSelection(start, new Date(viewDate.getFullYear(), (i + 1) * 3, 0))}
                              sx={{
                                py: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                display: "flex",
                                flexDirection: "column",
                                bgcolor: active ? colors.orange[100] : "var(--fx-surface-sunken, #F8FAFC)",
                                color: active ? colors.orange.base : activeTextColor,
                                "&:hover": { bgcolor: active ? colors.orange[100] : "rgba(255, 107, 0, 0.08)" },
                                "&:disabled": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)", color: disableColor },
                              }}
                            >
                              <Typography sx={{ fontSize: 11, fontWeight: 900 }}>{q.split(" ")[0]}</Typography>
                              <Typography sx={{ fontSize: 9, fontWeight: 700, opacity: 0.8 }}>
                                {MONTHS[start.getMonth()].slice(0, 3)} - {MONTHS[i * 3 + 2].slice(0, 3)}
                              </Typography>
                            </Button>
                          </span>
                        </Tooltip>
                      </Grid>
                    );
                  })}

                {mainMode === "year" &&
                  Array.from({ length: 9 }, (_, i) => viewDate.getFullYear() - 4 + i).map((y) => {
                    const start = new Date(y, 0, 1);
                    const active = getStatus(start, new Date(y, 11, 31)) !== "normal";
                    const disabled = isFuture(start);
                    return (
                      <Grid item xs={4} key={y}>
                        <Tooltip title={disabled ? "Future year" : ""} arrow>
                          <span style={{ cursor: disabled ? "not-allowed" : "pointer", width: "100%", display: "block" }}>
                            <Button
                              fullWidth
                              disabled={disabled}
                              onClick={() => handleSelection(start, new Date(y, 11, 31))}
                              sx={{
                                py: 1,
                                borderRadius: 2,
                                textTransform: "none",
                                display: "flex",
                                flexDirection: "column",
                                bgcolor: active ? colors.orange[100] : "var(--fx-surface-sunken, #F8FAFC)",
                                color: active ? colors.orange.base : activeTextColor,
                                "&:hover": { bgcolor: active ? colors.orange[100] : "rgba(255, 107, 0, 0.08)" },
                                "&:disabled": { bgcolor: "var(--fx-surface-sunken, #F8FAFC)", color: disableColor },
                              }}
                            >
                              <Typography sx={{ fontSize: 11, fontWeight: 900 }}>{y}</Typography>
                              <Typography sx={{ fontSize: 8, fontWeight: 700, opacity: 0.8 }}>Full Year</Typography>
                            </Button>
                          </span>
                        </Tooltip>
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          </Fade>
        ) : (
          <Fade in={showCard}>
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography sx={{ fontWeight: 900, color: "var(--fx-surface-texttertiary, #94A3B8)", fontSize: 9 }}>CONFIRM PERIOD</Typography>
                <IconButton size="small" onClick={() => setShowCard(false)} sx={{ color: colors.orange.base, bgcolor: colors.orange[50] }}>
                  <EditCalendar fontSize="small" />
                </IconButton>
              </Box>
              <Box flex={1} display="flex" alignItems="center" justifyContent="center">
                <SelectionSummary />
              </Box>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  if (range.start) {
                    const start = new Date(range.start);
                    const end = new Date(range.end || range.start);
                    applyRange(start, end, periodLabelFor(mainMode, isRangePicker, start));
                  }

                  setShowCard(false);
                }}
                sx={{
                  bgcolor: colors.orange.base,
                  textTransform: "none",
                  fontWeight: 900,
                  borderRadius: 3,
                  py: 1.2,
                  boxShadow: "none",
                  mb: 1,
                  "&: hover": { bgcolor: colors.orange[600], boxShadow: "none" },
                }}
              >
                Confirm Selection
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={applyReset}
                sx={{ color: "var(--fx-surface-texttertiary, #94A3B8)", textTransform: "none", fontWeight: 800, fontSize: 10 }}
              >
                Reset
              </Button>
            </Box>
          </Fade>
        )}
      </Paper>

      <Box display="flex" gap={1} width={width}>
        <Button
          fullWidth
          variant="outlined"
          onClick={applyToday}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            borderRadius: 2,
            color: colors.orange.base,
            borderColor: colors.orange.base,
            "&:hover": { borderColor: colors.orange.base, bgcolor: "rgba(255, 107, 0, 0.06)" },
          }}
        >
          Today
        </Button>
        <Button
          fullWidth
          variant="text"
          onClick={applyReset}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            fontSize: 12,
            borderRadius: 2,
            color: "var(--fx-surface-textsecondary, #64748B)",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            "&:hover": { bgcolor: "rgba(255, 107, 0, 0.05)", color: colors.orange.base },
          }}
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
}
